import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

interface TTSContextType {
  isSpeaking: boolean;
  currentMessageId: string | null;
  speak: (text: string, messageId?: string) => void;
  stop: () => void;
  isMessageSpeaking: (messageId: string) => boolean;
}

const TTSContext = createContext<TTSContextType | undefined>(undefined);

export const TTSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);

  const getBestVoice = () => {
    return new Promise<SpeechSynthesisVoice | null>((resolve) => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length) {
        const preferredVoice = voices.find(voice =>
          (voice.name.includes('Natural') || voice.name.includes('Enhanced')) &&
          (voice.name.includes('Female') ||
           voice.name.includes('Samantha') ||
           voice.name.includes('Jenny') ||
           voice.name.includes('Aria') ||
           voice.name.includes('Zira') ||
           voice.name.includes('Susan') ||
           voice.name.includes('Linda') ||
           voice.name.includes('Karen') ||
           voice.name.includes('Sarah') ||
           voice.name.includes('Emma')) &&
          (voice.lang === 'en-US' || voice.lang.startsWith('en-'))
        ) || voices.find(voice =>
          voice.lang === 'en-US' && 
          (voice.name.includes('Natural') || voice.name.includes('Enhanced'))
        ) || voices.find(voice =>
          voice.lang === 'en-US'
        ) || voices[0] || null;

        console.log('Selected TTS voice:', preferredVoice?.name, preferredVoice?.lang);
        resolve(preferredVoice);
      } else {
        window.speechSynthesis.onvoiceschanged = () => {
          const voices = window.speechSynthesis.getVoices();
          const preferredVoice = voices.find(voice =>
            (voice.name.includes('Natural') || voice.name.includes('Enhanced')) &&
            (voice.name.includes('Female') ||
             voice.name.includes('Samantha') ||
             voice.name.includes('Jenny') ||
             voice.name.includes('Aria') ||
             voice.name.includes('Zira') ||
             voice.name.includes('Susan') ||
             voice.name.includes('Linda') ||
             voice.name.includes('Karen') ||
             voice.name.includes('Sarah') ||
             voice.name.includes('Emma')) &&
            (voice.lang === 'en-US' || voice.lang.startsWith('en-'))
          ) || voices.find(voice =>
            voice.lang === 'en-US' && 
            (voice.name.includes('Natural') || voice.name.includes('Enhanced'))
          ) || voices.find(voice =>
            voice.lang === 'en-US'
          ) || voices[0] || null;

          console.log('Selected TTS voice (onvoiceschanged):', preferredVoice?.name, preferredVoice?.lang);
          resolve(preferredVoice);
        };
      }
    });
  };

  const speak = async (text: string, messageId?: string) => {
    if (currentMessageId === messageId && isSpeaking) {
      stop();
      return;
    }

    if (speechSynthesisRef.current) {
      window.speechSynthesis.cancel();
    }

    if (!text.trim()) return;

    const utterance = new SpeechSynthesisUtterance(text);
    speechSynthesisRef.current = utterance;

    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.volume = 0.8;
    utterance.voice = await getBestVoice();

    utterance.onstart = () => {
      setIsSpeaking(true);
      setCurrentMessageId(messageId || null);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setCurrentMessageId(null);
      speechSynthesisRef.current = null;
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error);
      setIsSpeaking(false);
      setCurrentMessageId(null);
      speechSynthesisRef.current = null;
    };

    window.speechSynthesis.speak(utterance);
  };

  const stop = () => {
    if (speechSynthesisRef.current) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setCurrentMessageId(null);
      speechSynthesisRef.current = null;
    }
  };

  const isMessageSpeaking = (messageId: string) => {
    return currentMessageId === messageId && isSpeaking;
  };

  useEffect(() => {
    return () => {
      if (speechSynthesisRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    loadVoices();
  }, []);

  return (
    <TTSContext.Provider value={{
      isSpeaking,
      currentMessageId,
      speak,
      stop,
      isMessageSpeaking
    }}>
      {children}
      
      {isSpeaking && (
        <button
          onClick={stop}
          className="global-tts-stop"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: '#ff4757',
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            padding: '10px 16px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(255, 71, 87, 0.3)',
            zIndex: 9999,
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            animation: 'fadeIn 0.3s ease'
          }}
          title="Stop reading"
        >
          ⏹️ Stop
        </button>
      )}
      
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </TTSContext.Provider>
  );
};

export const useTTS = () => {
  const context = useContext(TTSContext);
  if (context === undefined) {
    throw new Error('useTTS must be used within a TTSProvider');
  }
  return context;
};